import { useEffect, useState } from 'react'
import { captureFix, deviceId, fixQuality, qualityMessage, type Fix } from '../lib/geo'
import { db, localRefOptions } from '../lib/db'
import { queue } from '../lib/sync'
import { queueCorrection } from '../lib/outbox'
import { t } from '../lib/i18n'
import type { Household } from './HouseholdScreen'

/**
 * CORRECTING A MISTAKE — identity only.
 *
 * Name, sex of head, whether the record should still be counted, and where the
 * pin sits. Not the people counts, not the latrine: those are indicators with a
 * history, and overwriting them would silently rewrite the past. They live on
 * the "Record what has changed" screen instead.
 *
 * The correction is written to the phone and queued. The op_id is generated
 * once, inside the queue entry, and reused on every upload attempt — which is
 * what makes "tap Save, no signal, phone retries for two days" harmless: the
 * server recognises the repeat and changes nothing the second time.
 */

type RefOption = { list_code: string; code: string; label_en: string; label_km: string | null }

const STATUSES: Array<[string, string, string]> = [
  ['active', 'fix.active', 'fix.activeWhy'],
  ['inactive', 'fix.inactive', 'fix.inactiveWhy'],
  ['flagged', 'fix.flagged', 'fix.flaggedWhy'],
]

export function CorrectDetailsScreen({
  household,
  onDone,
  onCancel,
}: {
  household: Household
  onDone: (message: string) => void
  onCancel: () => void
}) {
  const [options, setOptions] = useState<RefOption[]>([])
  const [headName, setHeadName] = useState(household.head_name ?? '')
  const [headSex, setHeadSex] = useState(household.head_sex ?? '')
  const [status, setStatus] = useState(household.record_status)
  const [note, setNote] = useState('')

  const [fix, setFix] = useState<Fix | null>(null)
  const [capturing, setCapturing] = useState(false)
  const [progress, setProgress] = useState('')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    localRefOptions(['sex']).then((rows) => setOptions(rows as RefOption[]))
  }, [])

  const nameChanged = headName.trim() !== (household.head_name ?? '').trim()
  const sexChanged = headSex !== (household.head_sex ?? '')
  const statusChanged = status !== household.record_status
  const somethingChanged = nameChanged || sexChanged || statusChanged || fix !== null

  // A status change without a reason is a record nobody can interpret in a year.
  const noteRequired = statusChanged && status !== 'active'
  const noteMissing = noteRequired && note.trim() === ''

  async function grabLocation() {
    setError(null)
    setCapturing(true)
    setProgress('Starting…')
    const { promise } = captureFix({
      onProgress: (p) =>
        setProgress(
          p.best == null
            ? `Searching… ${p.seconds}s`
            : `Best so far ±${p.best} m · ${p.samples} readings · ${p.seconds}s`,
        ),
    })
    try {
      setFix(await promise)
      setProgress('')
    } catch (e) {
      setError((e as Error).message)
      setProgress('')
    } finally {
      setCapturing(false)
    }
  }

  async function save() {
    setError(null)
    setSaving(true)
    try {
      // The phone's own copy first, so the correction is visible immediately.
      await db.households.update(household.household_uid, {
        ...(nameChanged ? { head_name: headName.trim() || null } : {}),
        ...(sexChanged ? { head_sex: headSex || null } : {}),
        ...(statusChanged ? { record_status: status } : {}),
        ...(fix ? { lat: fix.lat, lon: fix.lon, gps_accuracy_m: fix.accuracy_m } : {}),
        updated_at: new Date().toISOString(),
        pending: true,
      })

      // Only send what actually changed. Every parameter left null means
      // "leave this alone", so an untouched field is never rewritten with the
      // value the screen happened to be showing.
      await queue(
        queueCorrection({
          household_uid: household.household_uid,
          head_name: nameChanged ? headName.trim() : null,
          head_sex: sexChanged ? headSex || null : null,
          record_status: statusChanged ? status : null,
          status_note: note.trim() || null,
          lat: fix?.lat ?? null,
          lon: fix?.lon ?? null,
          gps_accuracy_m: fix?.accuracy_m ?? null,
          gps_provider: fix?.provider ?? null,
          device: deviceId(),
        }),
      )

      onDone('Correction saved on this phone. The old value is kept in the change history.')
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const selectStyle = {
    width: '100%',
    minHeight: 48,
    fontSize: 16,
    borderRadius: 10,
    border: '2px solid var(--line)',
    padding: '10px 12px',
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{t('fix.title')} · {household.household_code ?? ''}</h1>
        <button className="btn-quiet" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>

      <div className="content">
        <div className="alert" style={{ borderColor: 'var(--line)' }}>
          {t('fix.onlyMistakes')}
        </div>

        <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t('fix.whoLives')}</h3>

          <label htmlFor="head">{t('add.headName')}</label>
          <input
            id="head"
            type="text"
            value={headName}
            onChange={(e) => setHeadName(e.target.value)}
          />

          <label htmlFor="sex">{t('add.headSex')}</label>
          <select id="sex" value={headSex} onChange={(e) => setHeadSex(e.target.value)} style={selectStyle}>
            <option value="">{t('common.notRecorded')}</option>
            {options.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label_en}
                {o.label_km ? ` · ${o.label_km}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t('fix.stillValid')}</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            {t('fix.neverDeleted')}
          </p>

          {STATUSES.map(([code, title, why]) => (
            <button
              key={code}
              onClick={() => setStatus(code)}
              className={status === code ? 'btn-primary' : ''}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                marginBottom: 8,
                ...(status === code
                  ? {}
                  : { border: '2px solid var(--line)', background: 'var(--white)' }),
              }}
            >
              <div style={{ fontWeight: 700 }}>{t(title)}</div>
              <div style={{ fontSize: 14, opacity: 0.85 }}>{t(why)}</div>
            </button>
          ))}

          {noteRequired && (
            <>
              <label htmlFor="note">{t('fix.whyRequired')}</label>
              <input
                id="note"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. house demolished, family moved to Siem Reap"
              />
            </>
          )}
        </div>

        <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t('fix.pinWrong')}</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            {t('fix.onlyIfHere')}
          </p>

          <button onClick={grabLocation} disabled={capturing || saving}>
            {capturing ? t('add.capturing') : fix ? t('add.captureAgain') : t('fix.captureHere')}
          </button>

          {progress && <p className="muted">{progress}</p>}

          {fix && (
            <div
              className={fixQuality(fix.accuracy_m) === 'poor' ? 'alert alert-warn' : 'alert'}
              style={fixQuality(fix.accuracy_m) === 'poor' ? undefined : { borderColor: 'var(--line)' }}
            >
              {qualityMessage(fix.accuracy_m)}
              <div className="muted" style={{ marginTop: 4 }}>
                The pin will move to {fix.lat.toFixed(5)}, {fix.lon.toFixed(5)} when you save.
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginTop: 14 }}>
            {error}
          </div>
        )}

        <div style={{ margin: '18px 0 40px' }}>
          <button
            className="btn-primary"
            onClick={save}
            disabled={saving || capturing || !somethingChanged || noteMissing}
          >
            {saving ? t('common.saving') : t('fix.submit')}
          </button>
          {!somethingChanged && <p className="muted">{t('fix.nothingChanged')}</p>}
          {noteMissing && <p className="muted">{t('fix.sayWhy')}</p>}
        </div>
      </div>
    </div>
  )
}
